import { formatDistance, formatDistanceValue, formatElevation, haversine } from "./utils.js";

const ROUTED_COLOR = "#16a34a";
const MANUAL_COLOR = "#d81b60";

export class RouteEditor {
  constructor({ root, map, routingService, elevationService, gpxExporter, renderProfile }) {
    this.root = root;
    this.map = map;
    this.routingService = routingService;
    this.elevationService = elevationService;
    this.gpxExporter = gpxExporter;
    this.renderProfileCallback = renderProfile;
    this.followInput = root.querySelector(".bhgpxrp__follow");
    this.undoBtn = root.querySelector(".bhgpxrp__undoBtn");
    this.clearBtn = root.querySelector(".bhgpxrp__clearBtn");
    this.exportBtn = root.querySelector(".bhgpxrp__exportBtn");
    this.status = root.querySelector(".bhgpxrp__status");
    this.stats = root.querySelector(".bhgpxrp__stats");
    this.kmOverview = root.querySelector("[data-km-overview]");
    this.chartWrap = root.querySelector(".bhgpxrp__chartWrap");
    this.chart = root.querySelector(".bhgpxrp__chart");
    this.waypoints = [];
    this.segments = [];
    this.history = [];
    this.markerLayer = L.featureGroup().addTo(map);
    this.segmentLayer = L.featureGroup().addTo(map);
    this.markers = new Map();
    this.segmentLayers = new Map();
    this.nextWaypointId = 1;
    this.nextSegmentId = 1;
    this.isBusy = false;
    this.dragSnapshot = null;
    this.ignoreMapClickUntil = 0;
    this.currentProfile = this.elevationService.buildProfile([], []);
  }

  boot() {
    this.map.on("click", (event) => this.handleMapClick(event));
    this.map.on("bhgpxrp:searchselected", () => {
      this.ignoreMapClickUntil = Date.now() + 500;
    });
    this.undoBtn.addEventListener("click", () => this.undo());
    this.clearBtn.addEventListener("click", () => this.clearRoute());
    this.exportBtn.addEventListener("click", () => this.exportGpx());
    this.updateUi("Click the map to add route waypoints.");
  }

  async handleMapClick(event) {
    if (Date.now() < this.ignoreMapClickUntil || isMapControlEvent(event.originalEvent)) {
      return;
    }

    if (this.isBusy) {
      this.setStatus("Please wait for the current segment to finish.", false);
      return;
    }

    await this.addWaypoint(event.latlng.lat, event.latlng.lng);
  }

  async addWaypoint(latitude, longitude) {
    const snapshot = this.getSnapshot();
    const waypoint = {
      id: `wp_${this.nextWaypointId++}`,
      latitude,
      longitude,
    };
    const previousWaypoint = this.waypoints[this.waypoints.length - 1];

    this.history.push(snapshot);
    this.waypoints.push(waypoint);
    this.renderWaypoint(waypoint, this.waypoints.length - 1);

    if (!previousWaypoint) {
      this.updateUi("First waypoint added. Click another point to create a route segment.");
      return;
    }

    const segment = {
      id: `seg_${this.nextSegmentId++}`,
      fromId: previousWaypoint.id,
      toId: waypoint.id,
      type: this.followInput.checked ? "routed" : "manual",
      requestedType: this.followInput.checked ? "routed" : "manual",
      coordinates: [],
      distance: 0,
      routingFallback: false,
      revision: 0,
    };
    this.segments.push(segment);

    try {
      await this.recalculateSegments([segment]);
      this.updateUi(segment.routingFallback ? "No nearby routable hiking path found. Added a direct segment instead." : "Route segment added.");
    } catch (error) {
      this.history.pop();
      this.restoreSnapshot(snapshot);
      this.setStatus(error.message || "Unable to add route segment.", true);
      console.error(error);
    }
  }

  async deleteWaypoint(id) {
    if (this.isBusy) {
      return;
    }

    const index = this.waypoints.findIndex((waypoint) => waypoint.id === id);
    if (index === -1) {
      return;
    }

    const snapshot = this.getSnapshot();
    const previous = this.waypoints[index - 1];
    const next = this.waypoints[index + 1];
    const incoming = previous ? this.getSegmentBetween(previous.id, id) : null;
    const outgoing = next ? this.getSegmentBetween(id, next.id) : null;

    this.history.push(snapshot);
    this.waypoints.splice(index, 1);
    this.segments = this.segments.filter((segment) => segment.fromId !== id && segment.toId !== id);

    const replacementSegments = [];
    if (previous && next) {
      const replacement = {
        id: `seg_${this.nextSegmentId++}`,
        fromId: previous.id,
        toId: next.id,
        type: incoming?.type || outgoing?.type || (this.followInput.checked ? "routed" : "manual"),
        requestedType: incoming?.requestedType || outgoing?.requestedType || (this.followInput.checked ? "routed" : "manual"),
        coordinates: [],
        distance: 0,
        routingFallback: false,
        revision: 0,
      };
      this.segments.splice(Math.max(index - 1, 0), 0, replacement);
      replacementSegments.push(replacement);
    }

    this.renderAll();

    try {
      if (replacementSegments.length) {
        await this.recalculateSegments(replacementSegments);
      }
      this.updateUi("Waypoint deleted.");
    } catch (error) {
      this.history.pop();
      this.restoreSnapshot(snapshot);
      this.setStatus(error.message || "Unable to update the route after deleting the waypoint.", true);
      console.error(error);
    }
  }

  async handleWaypointMoved(id, latlng) {
    if (!this.dragSnapshot) {
      const waypoint = this.waypoints.find((item) => item.id === id);
      const marker = this.markers.get(id);
      if (waypoint && marker) {
        marker.setLatLng([waypoint.latitude, waypoint.longitude]);
      }
      return;
    }

    const waypoint = this.waypoints.find((item) => item.id === id);
    if (!waypoint) {
      return;
    }

    const snapshot = this.dragSnapshot;
    this.dragSnapshot = null;
    waypoint.latitude = latlng.lat;
    waypoint.longitude = latlng.lng;
    this.history.push(snapshot);

    try {
      await this.recalculateSegments(this.getConnectedSegments(id));
      this.updateUi(this.getConnectedSegments(id).some((segment) => segment.routingFallback) ? "Waypoint moved. Some connected points were added as direct segments." : "Waypoint moved. Connected segments updated.");
    } catch (error) {
      this.history.pop();
      this.restoreSnapshot(snapshot);
      this.setStatus(error.message || "Unable to update moved waypoint.", true);
      console.error(error);
    }
  }

  async recalculateSegments(segments) {
    if (!segments.length) {
      this.renderAll();
      return;
    }

    this.setBusy(true);
    this.setStatus(segments.some((segment) => (segment.requestedType || segment.type) === "routed") ? "Calculating hiking route..." : "Updating manual segment elevation...", false);

    try {
      for (const segment of segments) {
        await this.recalculateSegment(segment);
      }
      this.renderAll();
    } finally {
      this.setBusy(false);
    }
  }

  async recalculateSegment(segment) {
    const from = this.waypoints.find((waypoint) => waypoint.id === segment.fromId);
    const to = this.waypoints.find((waypoint) => waypoint.id === segment.toId);
    if (!from || !to) {
      return;
    }

    segment.revision += 1;
    const revision = segment.revision;
    const result = await this.routingService.getSegment({
      type: segment.requestedType || segment.type,
      from,
      to,
    });

    if (!this.segments.includes(segment) || segment.revision !== revision) {
      return;
    }

    segment.type = result.type === "manual" ? "manual" : "routed";
    segment.routingFallback = Boolean(result.fallback);
    segment.coordinates = result.coordinates;
    segment.distance = Number.isFinite(result.distance) ? result.distance : calculateSegmentDistance(result.coordinates);
  }

  renderAll() {
    this.markerLayer.clearLayers();
    this.segmentLayer.clearLayers();
    this.markers.clear();
    this.segmentLayers.clear();

    this.segments.forEach((segment) => this.renderSegment(segment));
    this.waypoints.forEach((waypoint, index) => this.renderWaypoint(waypoint, index));
    this.updateProfile();
    this.updateButtons();
  }

  renderWaypoint(waypoint, index) {
    const marker = L.marker([waypoint.latitude, waypoint.longitude], {
      draggable: true,
      autoPan: true,
    });

    marker.bindTooltip(String(index + 1), {
      permanent: true,
      direction: "center",
      className: "bhgpxrp__waypointLabel",
    });
    marker.bindPopup(`<button class="bhgpxrp__deleteWaypoint" type="button" data-waypoint-id="${waypoint.id}">Delete waypoint</button>`);
    marker.on("dragstart", () => {
      if (!this.isBusy) {
        this.root.classList.add("bhgpxrp--dragging");
        this.dragSnapshot = this.getSnapshot();
      }
    });
    marker.on("dragend", () => {
      this.root.classList.remove("bhgpxrp--dragging");
      this.handleWaypointMoved(waypoint.id, marker.getLatLng());
    });
    marker.on("popupopen", () => {
      const button = marker.getPopup().getElement().querySelector(".bhgpxrp__deleteWaypoint");
      if (button) {
        button.addEventListener("click", () => this.deleteWaypoint(waypoint.id));
      }
    });
    marker.addTo(this.markerLayer);
    this.markers.set(waypoint.id, marker);
  }

  renderSegment(segment) {
    if (!Array.isArray(segment.coordinates) || segment.coordinates.length < 2) {
      return;
    }

    const layer = L.polyline(segment.coordinates.map((point) => [point.latitude, point.longitude]), {
      color: segment.type === "manual" ? MANUAL_COLOR : ROUTED_COLOR,
      dashArray: segment.type === "manual" ? "8 8" : null,
      lineCap: "round",
      lineJoin: "round",
      opacity: 0.95,
      smoothFactor: 1,
      weight: 5,
    });

    layer.bindTooltip(segment.routingFallback ? "Direct segment: no nearby routable hiking path found" : (segment.type === "manual" ? "Manual segment" : "Routed hiking segment"));
    layer.addTo(this.segmentLayer);
    this.segmentLayers.set(segment.id, layer);
  }

  updateProfile() {
    this.currentProfile = this.elevationService.buildProfile(this.waypoints, this.segments);
    this.stats.querySelector('[data-stat="distance"]').textContent = formatDistance(this.currentProfile.totalDistance);
    this.stats.querySelector('[data-stat="ascent"]').textContent = formatElevation(this.currentProfile.totalAscent);
    this.stats.querySelector('[data-stat="descent"]').textContent = formatElevation(this.currentProfile.totalDescent);
    this.stats.querySelector('[data-stat="min"]').textContent = formatElevation(this.currentProfile.minElevation);
    this.stats.querySelector('[data-stat="max"]').textContent = formatElevation(this.currentProfile.maxElevation);
    this.renderKilometerOverview();
    this.renderElevationProfile();
  }

  renderKilometerOverview() {
    if (!this.kmOverview) {
      return;
    }

    if (!this.currentProfile.kmSplits.length) {
      this.kmOverview.hidden = true;
      this.kmOverview.innerHTML = "";
      return;
    }

    this.kmOverview.hidden = false;
    this.kmOverview.innerHTML = this.currentProfile.kmSplits.map((split) => `
      <div class="bhgpxrp__kmStat" aria-label="${formatSplitRange(split)}">
        <span>${formatSplitRange(split)}</span>
        <small>${formatElevation(split.totalAscent)} up / ${formatElevation(split.totalDescent)} down</small>
      </div>
    `).join("");
  }

  renderElevationProfile() {
    this.renderProfileCallback(this.currentProfile, this.root, this.chartWrap, this.chart);
  }

  undo() {
    if (this.isBusy || !this.history.length) {
      return;
    }

    this.restoreSnapshot(this.history.pop());
    this.updateUi("Last action undone.");
  }

  clearRoute() {
    if (this.isBusy || (!this.waypoints.length && !this.segments.length)) {
      return;
    }

    this.history.push(this.getSnapshot());
    this.waypoints = [];
    this.segments = [];
    this.renderAll();
    this.updateUi("Route cleared. Click the map to start again.");
  }

  exportGpx() {
    try {
      this.gpxExporter.download(this.elevationService.getOrderedCoordinates(this.waypoints, this.segments));
      this.updateUi("GPX route downloaded.");
      this.trackExport();
    } catch (error) {
      this.setStatus(error.message || "Unable to export GPX.", true);
      console.error(error);
    }
  }

  getConnectedSegments(waypointId) {
    return this.segments.filter((segment) => segment.fromId === waypointId || segment.toId === waypointId);
  }

  getSegmentBetween(fromId, toId) {
    return this.segments.find((segment) => segment.fromId === fromId && segment.toId === toId) || null;
  }

  getSnapshot() {
    return {
      waypoints: this.waypoints.map((waypoint) => Object.assign({}, waypoint)),
      segments: this.segments.map((segment) => ({
        id: segment.id,
        fromId: segment.fromId,
        toId: segment.toId,
        type: segment.type,
        requestedType: segment.requestedType || segment.type,
        coordinates: segment.coordinates.map((point) => Object.assign({}, point)),
        distance: segment.distance,
        routingFallback: Boolean(segment.routingFallback),
        revision: segment.revision,
      })),
      nextWaypointId: this.nextWaypointId,
      nextSegmentId: this.nextSegmentId,
    };
  }

  restoreSnapshot(snapshot) {
    this.waypoints = snapshot.waypoints.map((waypoint) => Object.assign({}, waypoint));
    this.segments = snapshot.segments.map((segment) => ({
      id: segment.id,
      fromId: segment.fromId,
      toId: segment.toId,
      type: segment.type,
      requestedType: segment.requestedType || segment.type,
      coordinates: segment.coordinates.map((point) => Object.assign({}, point)),
      distance: segment.distance,
      routingFallback: Boolean(segment.routingFallback),
      revision: segment.revision || 0,
    }));
    this.nextWaypointId = snapshot.nextWaypointId;
    this.nextSegmentId = snapshot.nextSegmentId;
    this.renderAll();
  }

  updateUi(message) {
    this.setStatus(message, false);
    this.updateButtons();
  }

  updateButtons() {
    this.setButtonEnabled(this.undoBtn, this.history.length > 0 && !this.isBusy);
    this.setButtonEnabled(this.clearBtn, this.waypoints.length > 0 && !this.isBusy);
    this.setButtonEnabled(this.exportBtn, this.elevationService.getOrderedCoordinates(this.waypoints, this.segments).length >= 2 && !this.isBusy);
  }

  setBusy(isBusy) {
    this.isBusy = isBusy;
    this.root.classList.toggle("bhgpxrp--busy", isBusy);
    this.updateButtons();
  }

  setButtonEnabled(button, enabled) {
    button.disabled = !enabled;
    if (enabled) {
      button.removeAttribute("aria-disabled");
      return;
    }

    button.setAttribute("aria-disabled", "true");
  }

  setStatus(message, isError) {
    this.status.textContent = message;
    this.status.classList.toggle("bhgpxrp__status--error", Boolean(isError));
  }

  trackExport() {
    if (typeof window.gtag !== "function") {
      return;
    }

    window.gtag("event", "Export GPX", {
      event_category: "GPX Route Planner",
      event_label: "bh_gpx_route_planner",
    });
  }
}

function isMapControlEvent(event) {
  return Boolean(event?.target?.closest?.(".leaflet-control, .bhgpxrp__searchControl"));
}

function formatSplitRange(split) {
  return `${formatDistanceValue(split.startDistance)}-${formatDistanceValue(split.endDistance)} km`;
}

function calculateSegmentDistance(points) {
  let distance = 0;
  for (let index = 1; index < points.length; index += 1) {
    distance += haversine(points[index - 1], points[index]);
  }

  return distance;
}
